import { DataTablePrimeReact, TableERP } from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import { RemesaDtoProps } from "api/DTOs/RemesaDto"
import { RemesaFlujoTesoreriaDtoProps } from "api/DTOs/RemesaFlujoTesoreriaDto"
import { getFacturasByIdSimple } from "api/Factura.service"
import {
  useRemesaById,
  useRemesaFlujoTesoreriaById,
  useUpdateRemesaById,
} from "api/Remesa.hook"
import { exportRemesaFlujosTesoreriaToExcelById } from "api/Remesa.service"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import CustomButton from "common/components/Button/Button"
import HeaderWrapper from "common/components/HeaderWrapper/index"
import DeleteModal from "common/components/Modal/DeleteModal"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import { handleDirectDownloadExcel } from "common/context/DownloadFileProvider"
import TesoreriaService from "common/services/TesoreriaService"
import notification from "common/utils/notification"
import { requestHandler } from "common/utils/utils"
import { clearFacturaState } from "features/Documentos/redux/documentosSlice"
import { getTotals } from "features/Tesoreria/pages/Cartera/constants"
import { useDispatch } from "hooks/useDispatch"
import { Menu } from "primereact/menu"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { getFileNameFromContentDisposition } from "utils/getFileNameFromContentDisposition"
import { getModalSetup } from "./constants"
import { REMESA_DETAIL_COLUMNS } from "./remesaDetail.columns"
import { RemesaDetailById } from "./RemesaDetailById"
import "./style.scss"

const RemesaDetail = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const params = useParams<{ id?: string }>()
  const { data: remesaByIdData, refetch: remesaByIdRefetch } = useRemesaById(
    params?.id ?? ""
  )
  const {
    data: remesaFlujosByIdData,
    isLoading: remesaFlujosByIdIsLoading,
    refetch: remesaFlujosByIdRefetch,
  } = useRemesaFlujoTesoreriaById(params?.id ?? "")
  const updateRemesaById = useUpdateRemesaById()
  const [remesaForm, setRemesaForm] = useState<RemesaDtoProps>()
  const [remesaFormHasErrors, setRemesaFormHasErrors] = useState<boolean>(true)

  const handleRemesaFormUpdate = useCallback((values: RemesaDtoProps) => {
    setRemesaForm(values)
  }, [])

  const handleEditSave = async () => {
    if (!remesaByIdData) return
    const newData = { ...remesaByIdData, ...remesaForm }
    await updateRemesaById.mutateAsync({ id: params.id ?? "", remesa: newData })
    notification.success()
    remesaByIdRefetch()
  }

  const menu = useRef<Menu>(null)
  const dtRef = useRef<DataTablePrimeReact<
    RemesaFlujoTesoreriaDtoProps[]
  > | null>(null)

  const [total, setTotal] = useState<number>(0)
  const [showDelete, setShowDelete] = useState({
    id: null,
    status: false,
    key: "",
  })
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: FacturaReadingSimpleDtoProps | null
  }>({
    show: false,
    document: null,
  })

  const handleOpenDeleteModal = (
    id: $TSMigrationFixMe,
    status: $TSMigrationFixMe,
    key: $TSMigrationFixMe
  ) => {
    setShowDelete({ id, status, key })
  }

  const handleRedirectToList = () => {
    history.push(ROUTES.REMESAS)
  }

  useEffect(() => {
    const totalValue = getTotals(remesaFlujosByIdData, "importeRemesado")
    setTotal(totalValue)
  }, [remesaFlujosByIdData])

  useEffect(() => {
    return () => {
      dispatch(clearFacturaState())
      setTotal(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id])

  const onSuccessDeleteFlujo = () => {
    if (remesaFlujosByIdData?.length && remesaFlujosByIdData.length > 1) {
      remesaFlujosByIdRefetch()
    } else {
      handleRedirectToList()
    }
  }
  const onPostDelete = () => {
    setShowDelete({
      id: null,
      status: false,
      key: "",
    })
  }

  const handleDeleteFlujoTesoreria = async () => {
    if (showDelete.id) {
      requestHandler(
        {
          id: params.id,
          flujoTesoreriaId: showDelete.id,
        },
        TesoreriaService.deleteRemesaFlujoTesoreria,
        onSuccessDeleteFlujo,
        `${t("remesas.msg.deleteFlow")}`,
        null,
        null,
        onPostDelete
      )
    }
  }

  const handleDeleteRemesa = async () => {
    if (params.id) {
      requestHandler(
        params.id,
        TesoreriaService.deleteRemesa,
        handleRedirectToList,
        `${t("remesas.msg.deleteRemittance")}`,
        null,
        null,
        null
      )
    }
  }

  const handleDelete = (id: string) => {
    handleOpenDeleteModal(id, true, "pagos")
  }

  const modalDeleteSetup = getModalSetup(
    showDelete.status,
    setShowDelete,
    handleDeleteRemesa,
    handleDeleteFlujoTesoreria,
    remesaByIdData?.concepto,
    t
  )

  const isFlujoFromPrestamo = Boolean(!remesaFlujosByIdData?.[0]?.documento)

  const handleOpenDocumentoModal = async (id: string) => {
    const facturaById = await getFacturasByIdSimple(id)
    setModalDocumentoDetail({
      show: true,
      document: facturaById,
    })
  }

  return (
    <section>
      <div>
        <HeaderWrapper
          icon='arrow-left'
          onClickIcon={handleRedirectToList}
          title={t("remesas.edit.title")}
        >
          <div className='d-flex align-items-center buttons_container'>
            <p
              onClick={() => handleOpenDeleteModal(params.id, true, "remesas")}
            >
              {t("remesas.edit.deleteButton")}
            </p>
            <CustomButton
              onClick={() => {
                handleEditSave()
              }}
              className='tw-ml-2 !tw-bg-secondary hover:!tw-bg-secondary-over !tw-border-none tw-cursor-pointer'
              disabled={remesaFormHasErrors}
            >
              {t("remesas.edit.saveButton")}
            </CustomButton>
            <div className='tw-place-content-center tw-border-solid tw-border tw-rounded-md xl:tw-max-w-10 hover:!tw-bg-gray-100 tw-select-none'>
              <Menu
                popup
                ref={menu}
                id='popup_menu_remesa_detail'
                className='!tw-py-1'
                model={[
                  {
                    label: `${t("mayor.exportButton")}`,
                    icon: "pi pi-file-excel",
                    command: async () => {
                      const res = await exportRemesaFlujosTesoreriaToExcelById(
                        String(params.id)
                      )
                      const fileName =
                        getFileNameFromContentDisposition(
                          res.headers["content-disposition"]
                        ) ?? "DetalleRemesa.xlsx"
                      handleDirectDownloadExcel(res.data, fileName)
                    },
                  },
                ]}
              />
              <div
                className='tw-grid tw-place-content-center tw-cursor-pointer tw-size-10 tw-rounded-md'
                onClick={(event) => menu.current?.toggle(event)}
                aria-controls='popup_menu_remesa_detail'
                aria-haspopup
              >
                <FontAwesomeIcon
                  icon={["fal", "ellipsis-v"]}
                  aria-controls='popup_menu_remesa_detail'
                  aria-haspopup
                  className='tw-text-black tw-size-5'
                />
              </div>
            </div>
          </div>
        </HeaderWrapper>
        <div className='tw-p-6 tw-grid tw-grid-cols-3 tw-gap-8'>
          <div className='tw-col-span-2 tw-bg-white *:tw-rounded-lg'>
            <div className='tw-flex tw-flex-col tw-h-[calc(100dvh-250px)] tw-relative '>
              <div className='tw-h-full tw-min-h-96'>
                <TableERP
                  refTable={dtRef}
                  columns={REMESA_DETAIL_COLUMNS(
                    t,
                    handleDelete,
                    isFlujoFromPrestamo,
                    handleOpenDocumentoModal,
                    remesaByIdData?.concepto
                  )}
                  className='tw-h-full !tw-z-0'
                  columnResizeMode='expand'
                  removableSort
                  value={remesaFlujosByIdData || []}
                  showGridlines
                  lazy
                  dataKey='id'
                  scrollable
                  size='small'
                  loading={remesaFlujosByIdIsLoading}
                  emptyMessage={t("primeReact.table.emptyMessage")}
                />
              </div>
            </div>
          </div>
          <div className='detail-form_container'>
            {remesaByIdData?.concepto && total ? (
              <RemesaDetailById
                data={remesaByIdData}
                remesaTotal={remesaFlujosByIdData?.reduce(
                  (acc, curr) => acc + (curr.importeRemesado || 0),
                  0
                )}
                onChangeForm={handleRemesaFormUpdate}
                onChangeErrors={setRemesaFormHasErrors}
              />
            ) : null}
          </div>
        </div>
        <DeleteModal
          visible={modalDeleteSetup[showDelete.key]?.show}
          onHide={() =>
            modalDeleteSetup[showDelete.key]?.setShow({
              id: null,
              status: false,
              key: "",
            })
          }
          size='small'
          iconType={modalDeleteSetup[showDelete.key]?.image}
          buttonLabel={t("pagoCobro.confirmButton")}
          title={`${t("pagoCobro.deleteButton")}${
            modalDeleteSetup[showDelete.key]?.title || ""
          }`}
          message={modalDeleteSetup[showDelete.key]?.msj}
          onConfirm={modalDeleteSetup[showDelete.key]?.delete}
        />

        <ModalVerDocumento
          showModal={modalDocumentoDetail.show}
          onHide={() =>
            setModalDocumentoDetail({
              show: false,
              document: null,
            })
          }
          docType={TIPO_DOCUMENTO_ENUM.FACTURA}
          documentoID={modalDocumentoDetail.document?.id ?? null}
          typeDocumentTable='facturas'
        />
      </div>
    </section>
  )
}

export default RemesaDetail
